import { TableComponents, TableVirtuoso } from "react-virtuoso"
import { TableHeader, TableDataField } from "../../../components/Table/DataTable"
import { Skeleton } from "@mui/material"
import HeaderCell from "../../../components/Table/CellTypes/HeaderCell"
import { forwardRef, useEffect, useRef, useState } from "react"
import TextCell from "./ProtocolTableComponents/TextCell"
import StructureCell from "../../../components/Table/CellTypes/StructureCell"
import { Protocol } from "../../../api/models"
import InfoPopover from "./ProtocolTableComponents/InfoPopover"

export interface NewProtocolTableData {
    id: string | number,
    fields: TableDataField[],
    result: 'removed' | 'changed',
}


export default function ProtocolTable({
    isLoading,
    headers,
    tableData,
    protocol,
    selectedStage,
}: {
    isLoading: boolean,
    headers: TableHeader[],
    tableData: NewProtocolTableData[],
    protocol: Protocol | null,
    selectedStage: string,
}) {
    const tableContainerRef = useRef<any | null>(null);
    const [ isScrollable, setIsScrollable ] = useState(false);
    const [ atLeft, setAtLeft ] = useState(true);
    // const [ atRight, setAtRight ] = useState(false);

    useEffect(() => {
        const tableContainer = tableContainerRef.current;

        const checkScroll = () => {
            if (!tableContainer) return;
            const { scrollLeft, scrollWidth, clientWidth } = tableContainer;
            setIsScrollable(scrollWidth > clientWidth);
            setAtLeft(scrollLeft === 0);
            // setAtRight(scrollLeft + clientWidth >= scrollWidth - 1);
        }

        checkScroll();

        const resizeObserver = new ResizeObserver(checkScroll);
        if (tableContainer) {
            resizeObserver.observe(tableContainer);
        }

        tableContainer?.addEventListener('scroll', checkScroll);
        window.addEventListener('resize', checkScroll);

        return () => {
            resizeObserver.disconnect();
            tableContainer?.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        }
    }, []);
    

    const getTooltipInfo = (ordinal: number) => {
        if (protocol) {
            const stages = protocol.protocol_metadata!.stage_changes;
            if (stages && stages.length > 0) {
                const currentStage = stages.find(stage => stage.function! === selectedStage);
                if (currentStage && currentStage.standardization_records && currentStage.standardization_records!.length > 0) {
                    const records = currentStage.standardization_records!;
                    const matchingRecord = records.find(record => record.ordinal! === ordinal);
                    if (matchingRecord) {
                        return matchingRecord;
                    }

                }
            }
        }
    }
    
    const VirtuosoTableComponents: TableComponents<NewProtocolTableData> = {
        TableRow: forwardRef(({ item, ...props  }, ref) => (
            <tr
                {...props}
                className={`${item.result === 'removed' ? 'removed-row' : item.result === 'changed' ? 'changed-row' : 'table-body'} relative`}
            >
                {props.children}
            </tr>
        )),
        Table: (props) => (
            <table {...props} className='w-full'/>
        ),
    }

    function fixedHeaderContent() {
        return (
            <tr className="bg-secondary text-default">
                {isLoading ? (
                    <>
                    {Array.from(Array(6).keys()).map(key => (
                        <td key={key} className="py-4 px-6"><Skeleton variant='text' sx={{fontSize: '1rem'}} width='100%'/></td>
                    ))}
                    </>
                ) : (
                    <>
                    {headers.map((header, i) => {
                        if (header.isShown) {
                            return (
                                <th 
                                    key={i} 
                                    className={`relative bg-secondary ${i === 0 && 'sticky left-0 z-20'} ${isScrollable && !atLeft && 'shadow-left'}`}
                                >
                                    <HeaderCell
                                        header={header}
                                    />
                                </th>
                            )
                        } else {
                            return null;
                        }
                    })}
                    </>
                )}
            </tr>
        )
    }

    function rowContent(index: number, row: NewProtocolTableData) {
        const structureField = row.fields.find(field => field.type === 'structure');
        const record = getTooltipInfo(Number(row.id));
        return (
            <>
            {structureField && (
                <td className={`relative sticky left-0 ${isScrollable && !atLeft && 'shadow-left'}`}>
                    {record && (
                        <div className='absolute top-[12px] left-[14px]'>
                            <InfoPopover record={record}/>
                        </div>
                    )}
                    <StructureCell 
                        value={structureField.value as string}
                        id={`molecule-structure-svg-${row.id}`}
                        index={0}
                        hasPadding
                    />
                </td>
            )}
            {headers.filter(header => header.label !== 'Chemical Structure').map((header,i) => {
                const matchingData = row.fields.find(field => field.associatedColumn === header.value && field.type !== 'structure');
                if (matchingData && header.isShown) {
                    return (
                        <td>
                            <TextCell 
                                value={matchingData.value as string}
                                alignment='center'
                            />
                        </td>
                    )
                } else {
                    return null;
                }
            })} 
            </>
        )
    }

    return (
        <div className={`w-full transition-all h-full grow overflow-y-auto overflow-x-auto bg-white rounded-xl border border-outline`}>
            <TableVirtuoso 
                style={{ height: '100%', width: '100%' }}
                data={tableData}
                components={VirtuosoTableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={rowContent}
                scrollerRef={(ref) => {
                    tableContainerRef.current = ref;
                }}
            />
        </div>
    )
}